import styled, { css } from 'styled-components';

import Divider from '@/components/atom/Divider';
import MaterialIcon from '@/components/atom/MaterialIcon';
import { NonBenefitNonF2fDoctor } from '@/models/nonBenefit';
import theme from '@/styles/theme';
import { formatPriceComma } from '@/utils/format';

interface Props {
  doctor: NonBenefitNonF2fDoctor;
  treatmentMedicineUnitId: string;
  cheapestPrice: number;
  onClick: () => void;
  lastItem?: boolean;
}

const NonBenefitSearchDoctorItem = ({ doctor, onClick, lastItem, treatmentMedicineUnitId, cheapestPrice }: Props) => {
  return (
    <>
      <NonBenefitDoctorItemWrapper onClick={onClick}>
        <DoctorInfoWrapper>
          <DoctorNameWrapper>
            <DoctorName>{doctor.doctorDisplayName}</DoctorName>
            <DoctorHospitalName>{doctor.hospitalName}</DoctorHospitalName>
          </DoctorNameWrapper>
          <DoctorMedicinePriceWrapper>
            <DoctorMedicinePrice cheapest={doctor.isCheapestTreatmentPrice(treatmentMedicineUnitId, cheapestPrice)}>
              {doctor.getNonBenefitTreatmentPriceTextByTreatmentMedicineUnitId(treatmentMedicineUnitId)}
            </DoctorMedicinePrice>
            {doctor.isCheapestTreatmentPrice(treatmentMedicineUnitId, cheapestPrice) && (
              <DoctorMedicinePriceCheepWrapper>
                <MaterialIcon name={'local_offer'} size={14} filled color={theme.color.primary700} />
                <DoctorMedicinePriceCheap>닥터나우 최저가</DoctorMedicinePriceCheap>
              </DoctorMedicinePriceCheepWrapper>
            )}
          </DoctorMedicinePriceWrapper>
          <DoctorAddress>{doctor.hospitalAddress}</DoctorAddress>
          <DoctorDepartmentWrapper>
            {doctor.professionalDepartments.map((department) => (
              <DoctorDepartment key={`department-${department.name}`}>{department.name} 전문의</DoctorDepartment>
            ))}
            {doctor.readyForVideoCall && <DoctorVideoCall>영상진료</DoctorVideoCall>}
          </DoctorDepartmentWrapper>
        </DoctorInfoWrapper>
        <DoctorProfileImage src={doctor.profileImageUrl} />
      </NonBenefitDoctorItemWrapper>
      {!lastItem && <Divider color={theme.color.gray40} />}
    </>
  );
};
export default NonBenefitSearchDoctorItem;

interface EmptyProps {
  cheapestPrice: number;
  hospitalAddress: string;
  profileImageUrl: string;
  onClick: () => void;
}
const EmptyDoctorItem = ({ cheapestPrice, hospitalAddress, profileImageUrl, onClick }: EmptyProps) => {
  return (
    <NonBenefitDoctorItemWrapper onClick={onClick}>
      <DoctorInfoWrapper>
        <DoctorNameWrapper>
          <DoctorEmptyTitle>앱에서 상세 정보 확인 가능</DoctorEmptyTitle>
          <MaterialIcon name={'chevron_right'} color={theme.color.gray300} size={24} filled />
        </DoctorNameWrapper>
        <DoctorMedicinePriceWrapper>
          <DoctorMedicinePrice cheapest>{formatPriceComma(cheapestPrice)}원</DoctorMedicinePrice>
          <DoctorMedicinePriceCheepWrapper>
            <MaterialIcon name={'local_offer'} size={14} filled color={theme.color.primary700} />
            <DoctorMedicinePriceCheap>닥터나우 최저가</DoctorMedicinePriceCheap>
          </DoctorMedicinePriceCheepWrapper>
        </DoctorMedicinePriceWrapper>
        <DoctorAddress>{hospitalAddress}</DoctorAddress>
        <DoctorDepartmentWrapper>
          <DoctorVideoCall>영상진료</DoctorVideoCall>
        </DoctorDepartmentWrapper>
      </DoctorInfoWrapper>
      <DoctorProfileImage src={profileImageUrl} />
    </NonBenefitDoctorItemWrapper>
  );
};

NonBenefitSearchDoctorItem.Empty = EmptyDoctorItem;

const NonBenefitDoctorItemWrapper = styled.li`
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DoctorInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DoctorNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DoctorName = styled.h4`
  ${({ theme }) => theme.typography.UI16SB}
  color: ${({ theme }) => theme.color.gray600};
`;

const DoctorHospitalName = styled.p`
  ${({ theme }) => theme.typography.UI13M}
  color: ${({ theme }) => theme.color.gray300};
`;

const DoctorMedicinePriceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DoctorMedicinePrice = styled.p<{ cheapest?: boolean }>`
  ${({ theme }) => theme.typography.UI18SB}
  color: ${({ theme, cheapest }) => (cheapest ? theme.color.primary700 : theme.color.gray700)};
`;

const DoctorMedicinePriceCheepWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const DoctorMedicinePriceCheap = styled.p`
  ${({ theme }) => theme.typography.UI12SB}
  color: ${({ theme }) => theme.color.primary700};
`;

const DoctorAddress = styled.p`
  ${({ theme }) => theme.typography.UI12M}
  color: ${({ theme }) => theme.color.gray300};
`;

const DoctorDepartmentWrapper = styled.ul`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DoctorDepartment = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  height: 20px;
  ${({ theme }) => css`
    ${theme.typography.UI12M}
    color: ${theme.color.blue600};
    padding: 0 4px;
    background-color: ${theme.color.blue100};
  `}
`;

const DoctorVideoCall = styled(DoctorDepartment)`
  ${({ theme }) => css`
    color: ${theme.color.gray300};
    background-color: ${theme.color.gray30};
  `}
`;

const DoctorProfileImage = styled.div<{ src: string }>`
  width: 72px;
  height: 72px;
  background-image: url(${(props) => props.src});
  background-size: cover;
  border-radius: 50%;
`;

const DoctorEmptyTitle = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI16SB}
    color: ${theme.color.gray300};
  `}
`;
